import { ActionTree } from 'vuex'
import RootState from '@/store/RootState'
import UserState from "@/store/modules/account/UserState"
import { Auth } from "@/services/firebase/auth"
import router from "@/router"
import { DB } from "@/services/firebase/db"
import store from "@/store"

export const actions: ActionTree<UserState, RootState> = {
  register ({ commit }, payload) {
    commit('setAuthenticationError', null)
    Auth
      .createUserWithEmailAndPassword(payload.email, payload.password)
      .then((response: any) => {
        if (response && response.user) {
          if (payload.firstName && payload.lastName) {
            response.user.updateProfile({
              displayName: payload.firstName
            }).then(() => {
              DB.collection('users').doc(response.user.uid).set({
                firstName: payload.firstName,
                lastName: payload.lastName,
                email: payload.email,
                acceptedTermsAndConditionsOn: new Date(),
                isFirstLogin: true
              }, { merge: true })
            }).finally(() => {
              response.user.sendEmailVerification()
            })
          }
        }
        router.push({ name: 'login', params: { email: payload.email, password: payload.password, successMessageKey: 'account.successfullyRegistered' } })
      })
      .catch((error: any) => {
        commit('setAuthenticationError', error)
      })
  },
  login ({ commit }, payload) {
    commit('setAuthenticationError', null)
    Auth
      .signInWithEmailAndPassword(payload.email, payload.password)
      .then((response: any) => {
        if (response.user.emailVerified) {
          DB.collection('users').doc(response.user.uid).get()
            .then((documentSnapshot) => {
              if (documentSnapshot.exists) {
                const userData = documentSnapshot.data()
                commit('setUser', {
                  id: response.user.uid,
                  email: response.user.email,
                  firstName: userData?.firstName,
                  lastName: userData?.lastName,
                  profilePicture: userData?.profilePicture,
                  companyLogo: userData?.companyLogo,
                  companyColor: userData?.companyColor
                })
                store.dispatch("account/updateLastLogin")
                store.dispatch("account/updateMailInDb")
                router.push('/')
              } else {
                commit('setAuthenticationError', { code: 'auth/user-not-found' })
              }
            }).catch((error: any) => {
              commit('setAuthenticationError', error)
            })
        } else {
          commit('setAuthenticationError', { code: 'auth/user-not-verified' })
        }
      })
      .catch((error: any) => {
        commit('setAuthenticationError', error)
      })
  },
  resendMail ({ commit }) {
    commit('setAuthenticationError', null)
    if (!Auth.currentUser) {
      commit('setAuthenticationError', { code: 'auth/user-not-found' })
    }
    return Auth.currentUser?.sendEmailVerification()
      .catch((error: any) => {
        commit('setAuthenticationError', error)
      })
  },
  logout ({ commit }) {
    Auth
      .signOut()
      .then(() => {
        commit('setUser', null)
        commit('setAuthenticationError', null)
        // TODO remove app from store
        router.push('/login')
      })
      .catch((error) => {
        commit('setAuthenticationError', error.message)
      })
  },
  handleVerifyEmail ({ commit }, payload) {
    Auth.applyActionCode(payload)
      .then(() => {
        router.push({ name: 'login', params: { successMessageKey: 'account.successfullyVerified' } })
      }).catch((error) => {
        commit('setAuthenticationError', error)
      })
  },
  sendPasswordResetMail ({ commit }, email) {
    commit('setAuthenticationError', null)
    return Auth.sendPasswordResetEmail(email)
      .then(() => {
        return true
      })
      .catch((error) => {
        commit('setAuthenticationError', error)
      })
  },
  resetPassword ({ commit }, payload) {
    commit('setAuthenticationError', null)
    Auth.verifyPasswordResetCode(payload.oobCode).then((email) => {
      Auth.confirmPasswordReset(payload.oobCode, payload.newPassword)
        .then(() => {
          router.push({ name: 'login', params: { successMessageKey: 'account.successfullyResetPassword', mail: email } })
        }).catch((error) => {
          commit('setAuthenticationError', error)
        })
    }).catch((error) => {
      commit('setAuthenticationError', error)
    })
  },
  changeMail ({ commit }, newMail) {
    commit('setAuthenticationError', null)
    return Auth.currentUser?.updateEmail(newMail)
      .then(() => {
        return Auth.currentUser?.sendEmailVerification()
          .catch((error: any) => {
            commit('setAuthenticationError', error)
          })
      })
      .catch((error) => {
        commit('setAuthenticationError', error)
      })
  },
  changeFirstLogin ({ commit }) {
    commit('setAuthenticationError', null)
    const currentUser = Auth.currentUser
    if (!currentUser) {
      return
    }
    return DB.collection('users').doc(currentUser.uid).set({
      isFirstLogin: false
    }, { merge: true }).then(() => {
      commit('setUserFirstLogin', false)
    })
  },
  updateLastLogin ({ commit }) {
    commit('setAuthenticationError', null)
    const currentUser = Auth.currentUser
    if (!currentUser) {
      return
    }
    return DB.collection('users').doc(currentUser.uid).set({
      lastLogin: new Date(Date.now())
    }, { merge: true })
  },
  updateMailInDb ({ commit }) {
    commit('setAuthenticationError', null)
    const currentUser = Auth.currentUser
    if (!currentUser) {
      return
    }
    return DB.collection('users').doc(currentUser.uid).set({
      email: currentUser.email
    }, { merge: true })
  },
  handleRecoverEmail ({ commit }, oobCode) {
    commit('setAuthenticationError', null)
    let restoredEmail: any = null
    Auth.checkActionCode(oobCode)
      .then((info) => {
        restoredEmail = info.data.email
        Auth.applyActionCode(oobCode)
          .then(() => {
            Auth.sendPasswordResetEmail(restoredEmail)
              .then(() => {
                router.push({ name: 'login', params: { successMessageKey: 'account.successfullyRecoveredEMail' } })
              }).catch((error) => {
                commit('setAuthenticationError', error)
              })
          }).catch((error) => {
            commit('setAuthenticationError', error)
          })
      })
      .catch((error) => {
        commit('setAuthenticationError', error)
      })
  },
  deleteUserAccount ({ commit }) {
    commit('setAuthenticationError', null)
    const userId = Auth.currentUser?.uid
    return Auth.currentUser?.delete()
      .then(() => {
        return DB.collection('users').doc(userId).delete()
          .then(() => {
            router.push({ name: 'login', params: { successMessageKey: 'settings.account.successfullyDeleted' } })
          }).catch((error) => {
            commit('setAuthenticationError', error)
          })
      })
      .catch((error: any) => {
        commit('setAuthenticationError', error)
      })
  }
}
